/**
 * AI Agent for sketch editing
 * Implements a function-calling loop to assist with code generation
 */

import { AIService } from './ai-service';
import { ChatMessage, AI_TOOLS } from '@/types/ai';
import { ControlDefinition } from '@/types/api';
import { SiteVariant } from './site-config';

export interface AgentContext {
  sketchCode: string;
  sketchErrors: string | null;
  controls: ControlDefinition[];
  siteVariant: SiteVariant;
  systemPrompt: string; // System prompt is now passed in instead of loaded
  onCodeUpdate: (code: string, explanation: string) => void;
  onControlsUpdate: (controls: ControlDefinition[]) => void;
  onError: (error: string) => void;
}

interface FunctionExecutionResult {
  success: boolean;
  result: string;
  error?: string;
}

export class SketchAgent {
  private aiService: AIService;
  private context: AgentContext;
  private messages: ChatMessage[] = [];
  private maxIterations = 10; // Prevent infinite loops

  constructor(aiService: AIService, context: AgentContext) {
    this.aiService = aiService;
    this.context = context;
    
    // Initialize with system prompt from context
    this.messages.push({
      role: 'system',
      content: context.systemPrompt
    });
  }

  /**
   * Process a user message and execute the agent loop
   */
  async processMessage(userMessage: string): Promise<string> {
    // Add user message
    this.messages.push({
      role: 'user',
      content: userMessage
    });

    let iterations = 0;
    let finalResponse = '';

    while (iterations < this.maxIterations) {
      iterations++;

      try {
        // Get AI response with function calling
        const response = await this.aiService.chatCompletion(
          this.messages,
          AI_TOOLS,
          'auto'
        );

        // Add assistant response to conversation
        this.messages.push(response);

        // Check if there are function calls to execute
        if (response.tool_calls && response.tool_calls.length > 0) {
          // Execute each function call
          for (const toolCall of response.tool_calls) {
            const result = await this.executeFunction(
              toolCall.function.name,
              toolCall.function.arguments
            );

            // Add function result to conversation
            this.messages.push({
              role: 'tool',
              tool_call_id: toolCall.id,
              name: toolCall.function.name,
              content: result.success ? result.result : `Error: ${result.error}`
            });
          }

          // Continue loop to process function results
          continue;
        }

        // No function calls, we have the final response
        finalResponse = response.content || 'I apologize, I was unable to generate a response.';
        break;

      } catch (error) {
        console.error('Agent iteration error:', error);
        finalResponse = `I encountered an error: ${error instanceof Error ? error.message : 'Unknown error'}`;
        break;
      }
    }

    if (iterations >= this.maxIterations) {
      finalResponse += '\n\n(Reached maximum iteration limit)';
    }

    return finalResponse;
  }

  /**
   * Execute a function call
   */
  private async executeFunction(
    functionName: string,
    argsJson: string
  ): Promise<FunctionExecutionResult> {
    try {
      const args = JSON.parse(argsJson);

      switch (functionName) {
        case 'read_sketch':
          return this.readSketch();
        
        case 'edit_sketch_code':
          return this.editSketchCode(args.code, args.explanation);
        
        case 'get_sketch_errors':
          return this.getSketchErrors();
        
        case 'read_controls':
          return this.readControls();
        
        case 'add_control':
          return this.addControl(args.control);
        
        case 'update_control':
          return this.updateControl(args.name, args.updates);
        
        case 'remove_control':
          return this.removeControl(args.name);
        
        case 'set_controls':
          return this.setControls(args.controls);
        
        default:
          return {
            success: false,
            result: '',
            error: `Unknown function: ${functionName}`
          };
      }
    } catch (error) {
      return {
        success: false,
        result: '',
        error: `Failed to execute ${functionName}: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Function implementations
   */
  private readSketch(): FunctionExecutionResult {
    return {
      success: true,
      result: `Current sketch code:\n\`\`\`python\n${this.context.sketchCode}\n\`\`\``
    };
  }

  private editSketchCode(code: string, explanation: string): FunctionExecutionResult {
    try {
      // Validate code is not empty
      if (!code || code.trim().length === 0) {
        return {
          success: false,
          result: '',
          error: 'Code cannot be empty'
        };
      }

      // Update the sketch code through the callback
      this.context.onCodeUpdate(code, explanation);
      
      // Update our local context
      this.context.sketchCode = code;

      return {
        success: true,
        result: `Successfully updated sketch code. ${explanation}`
      };
    } catch (error) {
      return {
        success: false,
        result: '',
        error: error instanceof Error ? error.message : 'Failed to update code'
      };
    }
  }

  private getSketchErrors(): FunctionExecutionResult {
    if (!this.context.sketchErrors) {
      return {
        success: true,
        result: 'No errors detected in the current sketch.'
      };
    }

    return {
      success: true,
      result: `Sketch errors:\n${this.context.sketchErrors}`
    };
  }

  private readControls(): FunctionExecutionResult {
    if (!this.context.controls || this.context.controls.length === 0) {
      return {
        success: true,
        result: 'No controls are currently defined for this sketch.'
      };
    }

    const controlsList = this.context.controls.map(ctrl => {
      if (ctrl.type === 'numeric') {
        return `- ${ctrl.name}: ${ctrl.label} (${ctrl.type}, default: ${ctrl.default}, range: ${ctrl.min}-${ctrl.max}, step: ${ctrl.step || 1})`;
      }
      return `- ${ctrl.name}: ${ctrl.label} (${ctrl.type})`;
    }).join('\n');

    return {
      success: true,
      result: `Current controls:\n${controlsList}`
    };
  }

  private addControl(control: any): FunctionExecutionResult {
    try {
      // Validate control definition
      if (!control.type || !control.name || !control.label || control.value === undefined) {
        return {
          success: false,
          result: '',
          error: 'Control must have type, name, label, and value properties'
        };
      }

      // Check if control with same name already exists
      if (this.context.controls.some(c => c.name === control.name)) {
        return {
          success: false,
          result: '',
          error: `A control named "${control.name}" already exists. Use update_control to modify it.`
        };
      }

      // Convert to internal format (currently only numeric/slider supported)
      let newControl: ControlDefinition;

      if (control.type === 'slider' || control.type === 'numeric') {
        if (control.min === undefined || control.max === undefined || control.step === undefined) {
          return {
            success: false,
            result: '',
            error: 'Slider controls must have min, max, and step properties'
          };
        }

        newControl = {
          type: 'numeric',
          name: control.name,
          label: control.label,
          min: Number(control.min),
          max: Number(control.max),
          default: Number(control.value),
          step: Number(control.step)
        };
      } else {
        return {
          success: false,
          result: '',
          error: `Control type "${control.type}" is not yet supported. Currently only "slider" or "numeric" types are available.`
        };
      }

      // Add to controls array
      const updatedControls = [...this.context.controls, newControl];
      this.context.controls = updatedControls;
      this.context.onControlsUpdate(updatedControls);

      return {
        success: true,
        result: `Successfully added control "${control.name}" (${control.label}). The variable "${control.name}" is now available in the sketch code.`
      };
    } catch (error) {
      return {
        success: false,
        result: '',
        error: error instanceof Error ? error.message : 'Failed to add control'
      };
    }
  }

  private updateControl(name: string, updates: any): FunctionExecutionResult {
    try {
      const controlIndex = this.context.controls.findIndex(c => c.name === name);
      
      if (controlIndex === -1) {
        return {
          success: false,
          result: '',
          error: `Control "${name}" not found`
        };
      }

      // Update the control with new properties
      const updatedControl = { ...this.context.controls[controlIndex] };
      
      if (updates.label !== undefined) updatedControl.label = updates.label;
      
      if (updatedControl.type === 'numeric') {
        if (updates.value !== undefined) updatedControl.default = Number(updates.value);
        if (updates.min !== undefined) updatedControl.min = Number(updates.min);
        if (updates.max !== undefined) updatedControl.max = Number(updates.max);
        if (updates.step !== undefined) updatedControl.step = Number(updates.step);
      }

      const updatedControls = [...this.context.controls];
      updatedControls[controlIndex] = updatedControl;
      
      this.context.controls = updatedControls;
      this.context.onControlsUpdate(updatedControls);

      return {
        success: true,
        result: `Successfully updated control "${name}"`
      };
    } catch (error) {
      return {
        success: false,
        result: '',
        error: error instanceof Error ? error.message : 'Failed to update control'
      };
    }
  }

  private removeControl(name: string): FunctionExecutionResult {
    try {
      const controlIndex = this.context.controls.findIndex(c => c.name === name);
      
      if (controlIndex === -1) {
        return {
          success: false,
          result: '',
          error: `Control "${name}" not found`
        };
      }

      const updatedControls = this.context.controls.filter(c => c.name !== name);
      this.context.controls = updatedControls;
      this.context.onControlsUpdate(updatedControls);

      return {
        success: true,
        result: `Successfully removed control "${name}"`
      };
    } catch (error) {
      return {
        success: false,
        result: '',
        error: error instanceof Error ? error.message : 'Failed to remove control'
      };
    }
  }

  private setControls(controls: any[]): FunctionExecutionResult {
    try {
      // Validate and convert all controls
      const convertedControls: ControlDefinition[] = [];

      for (const control of controls) {
        if (!control.type || !control.name || !control.label || control.value === undefined) {
          return {
            success: false,
            result: '',
            error: `Invalid control definition: ${JSON.stringify(control)}`
          };
        }

        if (control.type === 'slider' || control.type === 'numeric') {
          if (control.min === undefined || control.max === undefined || control.step === undefined) {
            return {
              success: false,
              result: '',
              error: `Slider control "${control.name}" must have min, max, and step properties`
            };
          }

          convertedControls.push({
            type: 'numeric',
            name: control.name,
            label: control.label,
            min: Number(control.min),
            max: Number(control.max),
            default: Number(control.value),
            step: Number(control.step)
          });
        } else {
          return {
            success: false,
            result: '',
            error: `Control type "${control.type}" is not yet supported for control "${control.name}"`
          };
        }
      }

      this.context.controls = convertedControls;
      this.context.onControlsUpdate(convertedControls);

      return {
        success: true,
        result: `Successfully set ${convertedControls.length} control(s)`
      };
    } catch (error) {
      return {
        success: false,
        result: '',
        error: error instanceof Error ? error.message : 'Failed to set controls'
      };
    }
  }

  /**
   * Clear conversation history (keep only system prompt)
   */
  clearHistory(): void {
    this.messages = [this.messages[0]]; // Keep system prompt
  }

  /**
   * Get conversation history
   */
  getHistory(): ChatMessage[] {
    return [...this.messages];
  }
}
